<script setup>
import { ref, watch, onMounted } from 'vue'
import { useRole } from '@/composables/useRole'
import { createToaster } from "@meforma/vue-toaster"

const props = defineProps({
  isOpen: Boolean,
  roleData: { type: Object, default: null }
})

const emit = defineEmits(['close', 'save'])

const { getPermissionsApi, updateRolePermissionsApi, loading } = useRole()
const toaster = createToaster({ position: "top-right", duration: 2000 })

const allPermissions = ref([])
const selectedPermissionIds = ref([])
const fetchingPermissions = ref(false)

const fetchPermissions = async () => {
  try {
    fetchingPermissions.value = true
    const res = await getPermissionsApi({ limit: -1 })
    if (res.success) {
      allPermissions.value = res.data?.data || res.data || []
    }
  } catch (e) {
    console.error(e)
  } finally {
    fetchingPermissions.value = false
  }
}

watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    fetchPermissions()
    if (props.roleData && props.roleData.permissions) {
      selectedPermissionIds.value = props.roleData.permissions.map(p => p.id)
    } else {
      selectedPermissionIds.value = []
    }
  }
})

const closeForm = () => {
  emit('close')
}

const handleSubmit = async () => {
  if (!props.roleData?.id) return
  
  try {
    const res = await updateRolePermissionsApi(props.roleData.id, {
      permission_ids: selectedPermissionIds.value
    })
    
    if (res.success) {
      toaster.success("Cập nhật quyền thành công!")
      emit('save')
      closeForm()
    } else {
      toaster.error(res.error || "Thao tác thất bại")
    }
  } catch (err) {
    toaster.error("Có lỗi xảy ra")
  }
}

const togglePermission = (id) => {
  const index = selectedPermissionIds.value.indexOf(id)
  if (index > -1) {
    selectedPermissionIds.value.splice(index, 1)
  } else {
    selectedPermissionIds.value.push(id)
  }
}
</script>

<template>
  <div v-if="isOpen" class="form-sidebar-overlay" @click.self="closeForm">
    <div class="form-sidebar">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h4 class="fw-bold m-0">Manage Permissions</h4>
        <button class="btn-close" @click="closeForm"></button>
      </div>

      <div class="role-info mb-4 p-3 rounded shadow-sm border-start border-4 border-primary" style="background-color: #f0f7ff;">
        <p class="mb-1 text-dark"><i class="fa fa-shield me-2 text-primary"></i><strong>Role:</strong> {{ roleData?.display_name || roleData?.name }}</p>
        <p class="mb-0 text-muted small">{{ roleData?.description }}</p>
      </div>

      <div class="permissions-list mb-4" v-if="!fetchingPermissions">
        <label class="form-label fw-semibold mb-3">Select Permissions</label>
        <div class="row g-2">
            <div v-for="permission in allPermissions" :key="permission.id" class="col-12">
                <div class="permission-item p-2 border rounded d-flex align-items-center mb-1" 
                     :class="{ 'active-permission': selectedPermissionIds.includes(permission.id) }"
                     @click="togglePermission(permission.id)"
                     style="cursor: pointer;">
                    <input class="form-check-input me-3 mt-0" type="checkbox" 
                           :checked="selectedPermissionIds.includes(permission.id)"
                           @click.stop="togglePermission(permission.id)">
                    <div class="flex-grow-1">
                        <div class="fw-bold text-dark" style="font-size: 13px;">{{ permission.display_name || permission.name }}</div>
                        <div class="text-muted" style="font-size: 11px;">{{ permission.name }}</div>
                    </div>
                </div>
            </div>
        </div>
      </div>
      <div v-else class="text-center p-5">
          <i class="fa fa-spinner fa-spin fa-2x text-primary"></i>
          <p class="mt-2 text-muted">Loading permissions...</p>
      </div>

      <div class="d-flex gap-2 mt-auto pt-4">
        <button type="button" class="btn btn-secondary w-100" @click="closeForm" :disabled="loading">Cancel</button>
        <button type="button" class="btn btn-primary w-100" @click="handleSubmit" :disabled="loading || fetchingPermissions">
          <i v-if="loading" class="fa fa-spinner fa-spin me-1"></i>
          Save Changes
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.form-sidebar-overlay {
  position: fixed;
  top: 0; left: 0; width: 100%; height: 100%;
  background: rgba(0,0,0,0.4);
  z-index: 9999 !important;
}
.form-sidebar {
  position: fixed;
  top: 0; right: 0;
  width: 450px; height: 100%;
  background: white;
  padding: 30px;
  box-shadow: -5px 0 15px rgba(0,0,0,0.1);
  animation: slideIn 0.3s ease-out;
  display: flex;
  flex-direction: column;
}
.permissions-list {
    flex-grow: 1;
    overflow-y: auto;
    padding-right: 10px;
}
.permission-item {
    transition: all 0.2s ease;
    border-color: #eee !important;
}
.permission-item:hover {
    background-color: #f8f9fa;
    border-color: #ddd !important;
}
.active-permission {
    background-color: #f0f7ff !important;
    border-color: #3577f1 !important;
}
.active-permission .fw-bold {
    color: #3577f1 !important;
}
@keyframes slideIn {
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
}
</style>
